/* eslint-disable indent */
/* eslint-disable no-shadow */
/**
 *
 * TextField
 *
 */

import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { TextField as MuiTextField, Stack } from '@mui/material';
import { COLORS, newLayout } from '../../../theme';

function TextField({
  value,
  showCounter = true,
  maxLength,
  name,
  required = false,
  valid,
  error,
  helperText,
  success,
  rows = 6,
  placeholder,
  disabled,
  theme,
  sx,
  onBlur,
  testId,
  ...props
}) {
  const [isTooLong, setIsTooLong] = useState(false);
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (maxLength) {
      setIsTooLong(value?.length > maxLength && !disabled);
    }
  }, [value]);

  const getHelperText = () => {
    if (!active && valid === false && error) {
      if (typeof error === 'object') {
        return (
          <Stack spacing={0.5}>
            {Object.values(error).map((errorMessage) => (
              <div>{errorMessage}</div>
            ))}
          </Stack>
        );
      }
      return error;
    }
    if (valid && success) {
      return success;
    }
    return helperText;
  };

  const buildHelperText = (length) => {
    if (error) {
      return error;
    }

    if (maxLength) {
      return `${length} / ${maxLength} characters`;
    }

    return `${length} characters`;
  };

  const componentColor = useMemo(() => {
    if (disabled || active) return 'primary';

    switch (valid) {
      case true:
        return 'success';
      case false:
        return 'error';
      default:
        return 'primary';
    }
  }, [disabled, valid, active]);

  const [helper, setHelper] = useState(getHelperText());

  useEffect(() => {
    setHelper(getHelperText());
  }, [valid, error, helperText, active]);

  return (
    <MuiTextField
      onFocus={() => !disabled && setActive(true)}
      onBlur={() => {
        onBlur?.();
        if (!disabled) {
          setActive(false);
        }
      }}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => !disabled && setHover(false)}
      value={value}
      name={name}
      inputProps={{ maxLength, ...(testId && { 'data-testid': testId }) }}
      helperText={showCounter ? buildHelperText(value?.length) : helper}
      multiline
      rowsMax={rows}
      minRows={rows}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      error={error ? !!error : isTooLong}
      InputLabelProps={{
        color: componentColor,
      }}
      color={componentColor}
      sx={{
        width: '100%',
        '.MuiInputBase-root': {
          padding: theme.spacing.s4,
          fontWeight: 500,
          backgroundColor: COLORS.white,
          borderRadius: theme.borderRadius.secondary,
        },
        ...(hover &&
          valid === undefined && {
            '&[data-shrink="false"]': {
              color: COLORS.kinetikBlack,
            },
          }),
        ...(active && {
          '& .MuiFormLabel-asterisk': {
            color: COLORS.black30,
          },
        }),
        '.MuiFormHelperText-root:not(.Mui-disabled)': {
          color: COLORS.black50,
          '&.Mui-error': {
            color: theme.text.error,
          },
        },
        '.MuiInputBase-input': {
          '&::placeholder': {
            color: COLORS.kinetikBlack,
          },
        },
        ...(value?.length && {
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: newLayout.border.filled.color,
          },
        }),
        ...sx,
      }}
      {...props}
    />
  );
}

TextField.propTypes = {
  value: PropTypes.string,
  showCounter: PropTypes.bool,
  maxLength: PropTypes.number,
  onChange: PropTypes.func,
  name: PropTypes.string,
  required: PropTypes.bool,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  theme: PropTypes.object,
  valid: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  success: PropTypes.string,
  onBlur: PropTypes.func,
  sx: PropTypes.object,
  testId: PropTypes.string,
};

export default memo(withTheme(TextField));
